import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META, API } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Store, Loader2, Copy, RefreshCw, KeyRound, Code, ExternalLink, Eye, EyeOff } from "lucide-react";

function Field({ label, value, mask, testid }) {
  const { t } = useI18n();
  const [reveal, setReveal] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); toast.success(t("common.copied")); };
  const shown = mask && !reveal ? value.slice(0, 12) + "••••••••" : value;
  return (
    <div className="bg-[#0A0A0A] border border-zinc-800 rounded-md p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <code className="font-mono-ob text-sm text-[#00E5FF] truncate" dir="ltr" data-testid={testid}>{shown}</code>
        <div className="flex items-center gap-2 shrink-0">
          {mask && (
            <button onClick={() => setReveal((r) => !r)} className="text-zinc-500 hover:text-[#00E5FF]" data-testid={`${testid}-reveal`}>
              {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
          <button onClick={copy} className="text-zinc-500 hover:text-[#00E5FF]"><Copy size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export default function Merchant() {
  const { t, lang } = useI18n();
  const [merchant, setMerchant] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ business_name: "", settlement_account_id: "", webhook_url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, cu] = await Promise.all([api.get("/accounts"), api.get("/currencies")]);
      setAccounts(a.data.filter((x) => x.status !== "closed"));
      setCurrencies(cu.data);
      try {
        const m = await api.get("/merchant/me");
        setMerchant(m.data);
        setForm({ business_name: m.data.business_name, settlement_account_id: m.data.settlement_account_id, webhook_url: m.data.webhook_url || "" });
        const p = await api.get("/merchant/payments");
        setPayments(p.data);
      } catch { setMerchant(null); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const curMap = Object.fromEntries(currencies.map((c) => [c.code, c]));

  const register = async () => {
    if (!form.business_name.trim() || !form.settlement_account_id) return toast.error(t("common.error"));
    setBusy(true);
    try { await api.post("/merchant/register", form); toast.success(t("mer.registered")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try { await api.put("/merchant/settings", form); toast.success(t("mer.saved")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!window.confirm(t("mer.regenConfirm"))) return;
    try { const { data } = await api.post("/merchant/regenerate-keys"); setMerchant(data); toast.success(t("mer.regenerated")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testCharge = async () => {
    try {
      const { data } = await api.post("/v1/charges", { amount: 25, currency: merchant.settlement_currency, description: "Test charge" },
        { headers: { Authorization: `Bearer ${merchant.secret_key}` } });
      toast.success(t("mer.testChargeDone"));
      window.open(data.checkout_url, "_blank");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  if (!merchant) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Store className="text-[#00E5FF]" /> {t("mer.becomeTitle")}</h1>
          <p className="text-zinc-500 text-sm mt-1">{t("mer.becomeSub")}</p>
        </div>
        <div className="ob-card rounded-md p-7 space-y-5">
          <div>
            <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("mer.businessName")}</label>
            <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="merchant-name-input"
              className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("mer.settlement")}</label>
            <select value={form.settlement_account_id} onChange={(e) => setForm({ ...form, settlement_account_id: e.target.value })} data-testid="merchant-settlement-select"
              className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none">
              <option value="">{accounts.length ? "—" : t("mer.needAccount")}</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{`${CURRENCY_META[a.currency_code]?.flag || ""} ${a.account_number}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("mer.webhook")}</label>
            <input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} dir="ltr" placeholder="https://yoursite.com/webhook" data-testid="merchant-webhook-input"
              className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-end focus:border-[#00E5FF] outline-none" />
          </div>
          <button onClick={register} disabled={busy} data-testid="merchant-register-button"
            className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />} {t("mer.register")}
          </button>
        </div>
      </div>
    );
  }

  const curlSnippet = `curl -X POST ${API}/v1/charges \\
  -H "Authorization: Bearer ${merchant.secret_key.slice(0, 16)}..." \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 100, "currency": "${merchant.settlement_currency}", "description": "Order #1001", "success_url": "https://yoursite.com/success"}'`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Store className="text-[#00E5FF]" /> {merchant.business_name}</h1>
          <p className="text-zinc-500 text-sm mt-1">{t("mer.title")}</p>
        </div>
        <button onClick={testCharge} data-testid="test-charge-button"
          className="border border-zinc-700 text-white px-4 py-2.5 rounded-md hover:border-[#00E5FF] hover:text-[#00E5FF] flex items-center gap-2"><ExternalLink size={16} /> {t("mer.testCharge")}</button>
      </div>

      <div className="ob-card rounded-md p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><KeyRound size={18} className="text-[#00E5FF]" /> {t("mer.keys")}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label={t("mer.publicKey")} value={merchant.api_key} testid="merchant-public-key" />
          <Field label={t("mer.secretKey")} value={merchant.secret_key} mask testid="merchant-secret-key" />
        </div>
        <button onClick={regenerate} data-testid="regenerate-keys-button" className="mt-4 text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"><RefreshCw size={14} /> {t("mer.regenerate")}</button>
      </div>

      <div className="ob-card rounded-md p-6">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Code size={18} className="text-[#00E5FF]" /> {t("mer.apiDocs")}</h3>
        <p className="text-xs text-zinc-500 mb-3">{t("mer.apiDocsSub")}</p>
        <pre className="bg-black border border-zinc-800 rounded-md p-4 text-xs text-zinc-300 overflow-x-auto" dir="ltr"><code>{curlSnippet}</code></pre>
      </div>

      <div className="ob-card rounded-md p-6">
        <h3 className="text-lg font-bold text-white mb-4">{t("mer.settings")}</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-zinc-500">{t("mer.businessName")}</label>
            <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white focus:border-[#00E5FF] outline-none" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">{t("mer.settlement")}</label>
            <select value={form.settlement_account_id} onChange={(e) => setForm({ ...form, settlement_account_id: e.target.value })}
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white focus:border-[#00E5FF] outline-none">
              {accounts.map((a) => <option key={a.id} value={a.id}>{`${a.account_number}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">{t("mer.webhook")}</label>
            <input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} dir="ltr"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white text-end focus:border-[#00E5FF] outline-none" />
          </div>
        </div>
        <button onClick={saveSettings} disabled={busy} data-testid="save-merchant-settings"
          className="mt-4 bg-[#00E5FF] text-black font-bold px-6 py-2.5 rounded-md hover:bg-[#33EBFF] flex items-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} {t("mer.saveSettings")}
        </button>
      </div>

      <div className="ob-card rounded-md p-6">
        <h3 className="text-lg font-bold text-white mb-4">{t("mer.payments")} ({payments.length})</h3>
        {payments.length === 0 ? <p className="text-zinc-500 text-sm">{t("mer.noPayments")}</p> : (
          <div className="space-y-1" data-testid="merchant-payments-list">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-3 border-b border-zinc-900 last:border-0">
                <div>
                  <div className="text-sm text-white">{p.description || p.id.slice(0, 8)}</div>
                  <div className="text-xs text-zinc-500" dir="ltr">{p.customer_label || "—"} · {new Date(p.created_at).toLocaleString("en-GB")}</div>
                </div>
                <div className="text-end">
                  <div className="font-num font-semibold text-white" dir="ltr">{fmtMoney(p.amount, curMap[p.currency]?.decimals || 2)} {p.currency}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "paid" ? "text-emerald-400 bg-emerald-500/10" : "text-amber-400 bg-amber-500/10"}`}>{p.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
